import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Plus, Store as StoreIcon, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { myVendorStoresApi } from '../services/api';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

/**
 * StoreSwitcher — sidebar affordance that lets a multi-store vendor switch
 * between stores they own OR spin up a new one. Adds `?vendor_id=<id>` to
 * the current URL so `useVendorContext` picks it up automatically.
 */
const StoreSwitcher = ({ activeVendorId, activeStoreName, activeLogo }) => {
  const nav = useNavigate();
  const location = useLocation();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    try {
      const { stores } = await myVendorStoresApi.list();
      setStores(stores || []);
    } catch (e) {
      // Silent — this is a nice-to-have widget, not critical infra.
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const switchTo = (vid) => {
    if (vid === activeVendorId) return;
    // Use the query param so `useVendorContext` immediately reads it and the
    // rest of the portal (store fetch, order list, etc.) refetches for the
    // new store id.
    const url = new URL(window.location.origin + location.pathname + location.search);
    url.searchParams.set('vendor_id', vid);
    nav(url.pathname + '?' + url.searchParams.toString());
  };

  // Hide the switcher entirely if the user only owns one store — no need
  // for the extra chrome. But keep the "+ New store" option accessible via
  // a lightweight footer button, always.
  const showList = stores.length > 1;

  return (
    <div className="px-3 pt-3">
      {showList && (
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-emerald-900 hover:bg-emerald-800 text-left" data-testid="store-switcher-trigger">
            {activeLogo ? (
              <img src={activeLogo} alt="" className="h-7 w-7 rounded-full object-cover shrink-0 ring-1 ring-emerald-500" />
            ) : (
              <div className="h-7 w-7 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                <StoreIcon className="h-3.5 w-3.5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 leading-none">My stores · {stores.length}</div>
              <div className="text-[13px] font-semibold text-white truncate leading-tight mt-0.5">{activeStoreName || 'Pick a store'}</div>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64" data-testid="store-switcher-menu">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">My stores</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {loading && <div className="px-2 py-2 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>}
            {stores.map((s) => (
              <DropdownMenuItem key={s.id} onClick={() => switchTo(s.id)} className="flex items-start gap-2 py-2" data-testid={`store-switcher-item-${s.id}`}>
                {s.logo ? (
                  <img src={s.logo} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <StoreIcon className="h-3.5 w-3.5 text-emerald-700" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1">
                    {s.name}
                    {s.id === activeVendorId && <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold ml-1">● Active</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {[s.city, s.country].filter(Boolean).join(', ') || s.category || 'Store'}
                  </div>
                  {s.approval_status === 'pending' && (
                    <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1"><ShieldCheck className="h-2.5 w-2.5" /> Pending admin approval</div>
                  )}
                  {s.approval_status === 'rejected' && (
                    <div className="text-[10px] text-rose-700 mt-0.5 flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" /> Rejected</div>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setOpenCreate(true)} className="text-emerald-700 focus:text-emerald-800" data-testid="store-switcher-add">
              <Plus className="h-4 w-4 mr-2" /> Add another store
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {!showList && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenCreate(true)}
          className="w-full bg-emerald-900 hover:bg-emerald-800 text-emerald-200 border-emerald-800"
          data-testid="store-switcher-add-empty"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add another store
        </Button>
      )}

      <NewStoreDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={async (vendor) => {
          await load();
          setOpenCreate(false);
          switchTo(vendor.id);
        }}
      />
    </div>
  );
};

const NewStoreDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Restaurant');
  const [country, setCountry] = useState('Canada');
  const [state, setState] = useState('Ontario');
  const [city, setCity] = useState('Ottawa');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Enter a store name'); return; }
    setBusy(true);
    try {
      const { vendor, message } = await myVendorStoresApi.create({ name: name.trim(), category, country, state, city });
      toast.success(message || 'Store created — awaiting admin approval.');
      onCreated?.(vendor);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create store');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-store-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><StoreIcon className="h-5 w-5 text-emerald-600" /> Add another store</DialogTitle>
          <DialogDescription className="text-xs">
            Fill in the basics. Your new store starts in <span className="font-semibold text-amber-700">pending approval</span> — a ShipitNow admin will review it before sales are unlocked.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="store-name">Store name</Label>
            <Input id="store-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Napoli Pizza — Uptown" data-testid="new-store-name-input" autoFocus />
          </div>
          <div>
            <Label htmlFor="store-cat">Category</Label>
            <Input id="store-cat" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Restaurant, Grocery, Courier…" data-testid="new-store-category-input" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <Label>City (Ottawa or Ibadan only)</Label>
              <select
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                value={city}
                onChange={(e) => {
                  const v = e.target.value;
                  setCity(v);
                  if (v === 'Ottawa') { setState('Ontario'); setCountry('Canada'); }
                  else { setState('Oyo'); setCountry('Nigeria'); }
                }}
                data-testid="new-store-city-select"
              >
                <option value="Ottawa">Ottawa, Ontario · Canada</option>
                <option value="Ibadan">Ibadan, Oyo · Nigeria</option>
              </select>
              <div className="text-[11px] text-slate-500 mt-1">ShipitNow operates in Ottawa (Canada) and Ibadan (Nigeria) only.</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-store-submit-btn">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Create store
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoreSwitcher;
